import { useEffect } from "react";
import { DEFAULT_KEYWORDS, DEFAULT_OG_IMAGE, SITE_NAME, toAbsoluteUrl } from "@/lib/seo";

type JsonLd = Record<string, unknown>;

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
  keywords?: string;
  noindex?: boolean;
  schema?: JsonLd | JsonLd[];
};


const upsertMeta = (attribute: "name" | "property", key: string, content: string) => {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const getAbsoluteImageUrl = (image: string) => {
  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }
  return toAbsoluteUrl(image);
};

const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  schema,
}: SeoProps) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const url = toAbsoluteUrl(path);
    const imageUrl = getAbsoluteImageUrl(image);
    const robots = noindex ? "noindex, nofollow" : "index, follow";

    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertCanonical(url);

    // Remove any previously injected schema scripts
    document.head.querySelectorAll<HTMLScriptElement>(`script[data-seo-schema]`).forEach((s) => s.remove());

    if (schema) {
      const payload = Array.isArray(schema) ? schema : [schema];
      payload.forEach((item, index) => {
        const script = document.createElement("script");
        script.setAttribute("data-seo-schema", String(index));
        script.type = "application/ld+json";
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }
  }, [description, image, keywords, noindex, path, schema, title, type]);

  return null;
};

export default Seo;
